import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { getDeck, getCards, addCard, deleteCard, Card, Deck as DeckBase, updateDeckStats, updateDeck, deleteDeck, addPublicDeckToUser, isDeckInUserLibrary, removeUserDeck, getUserStarredCards, toggleCardStar } from '../services/deckService';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase.config';
import { getUserInfo } from '../services/firebase';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Chip,
  Snackbar,
  Tooltip,
  useMediaQuery,
  Theme,
  useTheme
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCardDialog from '../components/AddCardDialog';
import ImportCardsDialog from '../components/ImportCardsDialog';
import DeckForm from '../components/DeckForm';
import UploadIcon from '@mui/icons-material/Upload';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PersonIcon from '@mui/icons-material/Person';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import ClassIcon from '@mui/icons-material/Class';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import { Link as RouterLink } from 'react-router-dom';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import SwipeRightIcon from '@mui/icons-material/SwipeRight';

// @ts-ignore - Игнорируем ошибку типизации для notistack
import { useSnackbar } from 'notistack';
import { useSwipeable } from 'react-swipeable';

// Расширяем тип Deck
interface Deck extends DeckBase {
  creatorName?: string;
  createdAt?: Date | string;
  lastStudied?: string;
  popularity?: number;
}

// Компонент для отдельной карточки
const CardItem: React.FC<{
  card: Card;
  index: number;
  isDuplicateCard: (card: Card, index: number) => boolean;
  isOwner: boolean;
  isMobile: boolean;
  deckSideNames: string[];
  onDelete: (cardId: string) => void;
  onUpdateCard: (card: Card) => void;
  onToggleStar: (cardId: string) => void;
  userStarredCards: string[];
}> = ({ card, index, isDuplicateCard, isOwner, isMobile, deckSideNames, onDelete, onUpdateCard, onToggleStar, userStarredCards }) => {
  // Добавляем состояние для анимации свайпа
  const [swipeProgress, setSwipeProgress] = useState(0);
  // Отслеживаем начальную скорость свайпа для быстрых свайпов
  const [swipeVelocity, setSwipeVelocity] = useState(0);
  // Отслеживаем, происходит ли скролл или свайп
  const [isScrolling, setIsScrolling] = useState(false);
  const [isSwiping, setIsSwiping] = useState(false);
  // Сохраняем начальные координаты касания
  const touchStartRef = useRef<{x: number, y: number} | null>(null);
  
  // Проверяем, отмечена ли карточка звездочкой пользователем
  const isStarred = userStarredCards.includes(card.id);
  
  // Обработчики для отслеживания скролла
  const touchStartHandler = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY
      };
    }
  }, []);

  const touchMoveHandler = useCallback((e: React.TouchEvent) => {
    if (touchStartRef.current && e.touches.length === 1 && !isSwiping) {
      const touchStart = touchStartRef.current;
      const currentY = e.touches[0].clientY;
      const deltaY = Math.abs(currentY - touchStart.y);
      
      // Если вертикальное движение значительное, отмечаем как скролл
      if (deltaY > 10) {
        setIsScrolling(true);
      }
    }
  }, [isSwiping]);

  const touchEndHandler = useCallback(() => {
    touchStartRef.current = null;
    // Сбрасываем флаг скролла через задержку
    setTimeout(() => {
      setIsScrolling(false);
    }, 150);
  }, []);
  
  // Используем хук useSwipeable с направлением свайпа справа налево
  const swipeHandlers = useSwipeable({
    onSwipeStart: (eventData) => {
      // Начало свайпа только если не в режиме скролла
      if (!isScrolling) {
        setSwipeVelocity(0);
        // При начале свайпа блокируем возможность скролла
        if (Math.abs(eventData.deltaX) > 10 && Math.abs(eventData.deltaY) < 5) {
          setIsSwiping(true);
        }
      }
    },
    onSwiping: (eventData) => {
      // Обрабатываем свайп только если не в режиме скролла
      if (isOwner && isMobile && !isScrolling) {
        // Если начали свайп горизонтально, отмечаем это
        if (!isSwiping && Math.abs(eventData.deltaX) > 10 && Math.abs(eventData.deltaY) < 5) {
          setIsSwiping(true);
        }
        
        // Проверяем направление свайпа - свайп вправо
        if (eventData.deltaX > 0 && (isSwiping || !isScrolling)) {
          // Свайп вправо
          setSwipeVelocity(Math.abs(eventData.velocity));
          
          // Получаем прогресс свайпа (максимум 150px)
          const progress = Math.min(eventData.deltaX, 150) / 150;
          setSwipeProgress(Math.min(1, Math.max(0, progress)));
        } else {
          setSwipeProgress(0);
        }
      }
    },
    onSwipedRight: (eventData) => {
      // Удаляем только если не в режиме скролла
      if (!isScrolling) {
        const isProgressSufficient = swipeProgress > 0.4;
        const isQuickSwipe = swipeVelocity > 0.5;
        
        if (isOwner && isMobile && (isProgressSufficient || isQuickSwipe)) {
          onDelete(card.id);
        }
      }
      setSwipeProgress(0);
      setSwipeVelocity(0);
      setIsSwiping(false);
    },
    onSwiped: () => {
      setSwipeProgress(0);
      setSwipeVelocity(0);
      setIsSwiping(false);
    },
    trackMouse: false,
    delta: 10,
    preventScrollOnSwipe: false,
    touchEventOptions: { passive: true }
  });

  // Цвет фона в зависимости от прогресса свайпа
  const getBackgroundColor = () => {
    if (swipeProgress > 0) {
      // Фон постепенно становится красным при свайпе
      const redAlpha = Math.min(0.8, swipeProgress * 0.8);
      return `rgba(244, 67, 54, ${redAlpha})`;
    }
    return isDuplicateCard(card, index) ? 'rgba(255, 152, 0, 0.1)' : 'inherit';
  };
  
  // Рассчитываем трансформацию для свайпа
  const getTransform = () => {
    if (swipeProgress > 0) {
      // Карточка сдвигается вправо при свайпе
      return `translateX(${swipeProgress * 30}px)`;
    }
    return 'translateX(0)';
  };

  return (
    <Box 
      {...(isMobile && isOwner ? swipeHandlers : {})}
      onTouchStart={touchStartHandler}
      onTouchMove={touchMoveHandler}
      onTouchEnd={touchEndHandler}
      className="card-item-row"
      sx={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? `5px repeat(${deckSideNames.length}, 1fr)` : `40px repeat(${deckSideNames.length}, 1fr)`,
        borderBottom: 1,
        borderColor: 'divider',
        p: 2,
        bgcolor: getBackgroundColor(),
        '&:hover': {
          bgcolor: swipeProgress > 0 ? getBackgroundColor() : isDuplicateCard(card, index) ? 'rgba(255, 152, 0, 0.2)' : 'action.hover',
        },
        position: 'relative',
        transition: 'all 0.2s ease',
        transform: getTransform(),
        opacity: swipeProgress > 0.5 ? 0.9 : 1,
        '&:hover .delete-button': {
          opacity: 1
        }
      }}
    >
      {/* Иконка удаления, появляющаяся при свайпе (только для мобильных) */}
      {isMobile && isOwner && swipeProgress > 0.1 && (
        <Box
          sx={{
            position: 'absolute',
            left: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            opacity: swipeProgress,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <DeleteIcon />
        </Box>
      )}
      
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        color: swipeProgress > 0.3 ? 'white' : 'inherit',
        transition: 'color 0.2s ease',
        margin: isMobile ? '-8px' : '0', // Компенсируем отступы кнопки на мобильных
      }}>
        <IconButton
          size="small"
          onClick={() => onToggleStar(card.id)}
          color={isStarred ? "warning" : "default"}
          sx={{
            color: swipeProgress > 0.3 ? 'white' : isStarred ? 'warning.main' : 'inherit',
            padding: isMobile ? '4px' : '8px', // Уменьшаем отступы кнопки на мобильных
          }}
        >
          {isStarred ? 
            <StarIcon sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }} /> : 
            <StarBorderIcon sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }} />
          }
        </IconButton>
      </Box>
      
      {card.sides.map((side, index) => (
        <Typography 
          key={index} 
          sx={{ 
            px: 2, 
            py: 1,
            color: swipeProgress > 0.3 ? 'white' : 'inherit', // Текст становится белым при свайпе
            transition: 'color 0.2s ease'
          }}
        >
          {side.content}
        </Typography>
      ))}
      
      {isOwner && !isMobile && (
        <IconButton
          className="delete-button"
          size="small"
          onClick={() => onDelete(card.id)}
          sx={{
            position: 'absolute',
            right: -16,
            top: '50%',
            transform: 'translateY(-50%)',
            bgcolor: 'error.main',
            color: 'white',
            width: 32,
            height: 32,
            opacity: 0,
            transition: 'opacity 0.2s',
            '&:hover': {
              bgcolor: 'error.dark',
            },
            display: { xs: 'none', md: 'flex' },
            boxShadow: 2
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

const DeckDetailsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { deckId } = useParams<{ deckId: string }>();
  const [deck, setDeck] = useState<Deck | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [cardToDelete, setCardToDelete] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isClearDialogOpen, setIsClearDialogOpen] = useState(false);
  const [isDeleteDeckDialogOpen, setIsDeleteDeckDialogOpen] = useState(false);
  const [authorName, setAuthorName] = useState<string>('');
  const [isInUserLibrary, setIsInUserLibrary] = useState(false);
  const [isAddToLibraryLoading, setIsAddToLibraryLoading] = useState(false);
  const [isRemoveFromLibraryDialogOpen, setIsRemoveFromLibraryDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const { enqueueSnackbar } = useSnackbar();
  
  // Добавляем состояние для отмеченных звездочкой карточек
  const [userStarredCards, setUserStarredCards] = useState<string[]>([]);
  
  // Определяем тему и статус мобильного устройства как в ClassDetailsPage
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Функция для переключения звездочки
  const handleToggleStar = useCallback(async (cardId: string) => {
    if (!user || !deckId) return;
    
    try {
      // Вызываем метод переключения звездочки в базе данных
      const success = await toggleCardStar(user.uid, deckId, cardId);
      
      if (success) {
        // Обновляем локальное состояние
        setUserStarredCards(prevStarred => {
          if (prevStarred.includes(cardId)) {
            return prevStarred.filter(id => id !== cardId);
          } else {
            return [...prevStarred, cardId];
          }
        });
        
        // Показываем уведомление об успехе
        enqueueSnackbar(t('cards.starToggled') || 'Card star toggled successfully', { variant: 'success' });
      } else {
        enqueueSnackbar(t('errors.starToggleFailed') || 'Failed to toggle card star', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error toggling card star:', error);
      enqueueSnackbar(t('errors.starToggleFailed') || 'Failed to toggle card star', { variant: 'error' });
    }
  }, [user, deckId, enqueueSnackbar, t]);
  
  // Перемещаем хук useCallback наверх перед условными return
  const handleUpdateCard = useCallback(async (updatedCard: Card) => {
    if (!deck || !deckId) return;
    
    try {
      const docRef = doc(db, 'decks', deckId, 'cards', updatedCard.id);
      await updateDoc(docRef, { ...updatedCard });
      
      // Обновляем состояние в интерфейсе
      setCards(prevCards => 
        prevCards.map(card => 
          card.id === updatedCard.id ? updatedCard : card
        )
      );
      
      // Показываем уведомление
      enqueueSnackbar(t('decks.cardUpdated'), { variant: 'success' });
    } catch (error) {
      console.error('Error updating card:', error);
      enqueueSnackbar(t('errors.updateCardFailed'), { variant: 'error' });
    }
  }, [deck, deckId, enqueueSnackbar, t]);

  useEffect(() => {
    const fetchDeck = async () => {
      if (deckId) {
        const deckData = await getDeck(deckId);
        if (deckData) {
          let finalDeckData = deckData as Deck;
          
          // Проверяем, является ли колода частью класса (для всех пользователей)
          if (user) {
            try {
              // Проверяем, есть ли у пользователя эта колода в userDecks с информацией о классе
              const userDeckRef = doc(db, 'userDecks', user.uid, 'decks', deckId);
              const userDeckSnap = await getDoc(userDeckRef);
              
              if (userDeckSnap.exists()) {
                const userDeckData = userDeckSnap.data();
                // Если колода из класса, обновляем информацию
                if (userDeckData.fromClassId && userDeckData.fromClassName) {
                  finalDeckData = {
                    ...deckData,
                    fromClassId: userDeckData.fromClassId,
                    fromClassName: userDeckData.fromClassName
                  } as Deck;
                  console.log("Колода из класса:", userDeckData.fromClassName);
                }
              }
            } catch (error) {
              console.error("Ошибка при проверке колоды в userDecks:", error);
            }
          }
          
          setDeck(finalDeckData);
          const cardsData = await getCards(deckId);
          setCards(cardsData);
          
          // Получаем информацию об авторе колоды
          if (finalDeckData.userId) {
            const userInfo = await getUserInfo(finalDeckData.userId);
            setAuthorName(userInfo?.displayName || 'Unknown User');
          }
          
          // Проверяем, добавлена ли колода в библиотеку пользователя
          if (user && finalDeckData.userId !== user.uid) {
            const inLibrary = await isDeckInUserLibrary(user.uid, deckId);
            setIsInUserLibrary(inLibrary);
          }
          
          // Загружаем отмеченные пользователем карточки
          if (user) {
            const starredCards = await getUserStarredCards(user.uid, deckId);
            setUserStarredCards(starredCards);
          }
        } else {
          setError('Deck not found');
        }
        setIsLoading(false);
      }
    };

    fetchDeck();
  }, [deckId, user]);

  if (!user) {
    navigate('/');
    return null;
  }

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/decks')}
            sx={{ mt: 2 }}
          >
            Back to Decks
          </Button>
        </Box>
      </Container>
    );
  }

  if (!deck) {
    return null;
  }

  const isOwner = deck.userId === user.uid;
  
  const handleAddCard = async (sides: string[]) => {
    if (deckId) {
      const newCard = await addCard(deckId, sides);
      if (newCard) {
        const formattedCard: Card = {
          id: newCard.id,
          sides: sides.map((content, index) => ({
            name: deck?.sideNames[index] || `Side ${index + 1}`,
            content
          })),
          isLearned: false,
          difficulty: 0,
          easyStreak: 0,
          isStarred: false,
          createdAt: Date.now()
        };
        setCards([...cards, formattedCard]);
        setIsAddDialogOpen(false);
      }
    }
  };

  const handleDeleteCard = async () => {
    if (deckId && cardToDelete) {
      const success = await deleteCard(deckId, cardToDelete);
      if (success) {
        setCards(cards.filter(card => card.id !== cardToDelete));
        setIsDeleteDialogOpen(false);
      }
    }
  };

  const handleImportCards = async (cards: string[][]) => {
    if (deckId) {
      let importedCount = 0;
      for (const cardSides of cards) {
        await addCard(deckId, cardSides);
        importedCount++;
      }
      
      // Reload cards
      const cardsData = await getCards(deckId);
      setCards(cardsData);
      setIsImportDialogOpen(false);
      
      setSnackbar({
        open: true,
        message: `Imported ${importedCount} cards successfully!`,
        severity: 'success'
      });
    }
  };

  const isDuplicateCard = (card: Card, index: number) => {
    for (let i = 0; i < index; i++) {
      if (cards[i].sides[0].content === card.sides[0].content) {
        return true;
      }
    }
    return false;
  };

  const handleUpdateDeck = async (data: {
    title: string;
    description: string;
    sideNames: string[];
    tags: string[];
    isPublic?: boolean;
  }) => {
    if (deckId) {
      const updatedDeck = await updateDeck(deckId, data);
      if (updatedDeck) {
        setDeck({ ...deck, ...(updatedDeck as unknown as Deck) });
        setIsEditDialogOpen(false);
        
        setSnackbar({
          open: true,
          message: 'Deck updated successfully!',
          severity: 'success'
        });
      }
    }
  };

  const handleClearDeck = async () => {
    if (deckId) {
      // Удаляем все карточки
      const promises = cards.map(card => deleteCard(deckId, card.id));
      await Promise.all(promises);
      
      // Очищаем карточки
      setCards([]);
      
      // Обновляем статистику колоды
      await updateDeckStats(deckId);
      
      // Закрываем диалог
      setIsClearDialogOpen(false);
      
      setSnackbar({
        open: true,
        message: 'All cards have been removed from the deck.',
        severity: 'success'
      });
    }
  };

  const handleOpenDeleteDeckDialog = () => {
    setIsDeleteDeckDialogOpen(true);
  };

  const handleCloseDeleteDeckDialog = () => {
    setIsDeleteDeckDialogOpen(false);
  };

  const handleDeleteDeck = async () => {
    if (deckId) {
      const success = await deleteDeck(deckId);
      if (success) {
        navigate('/decks');
      }
    }
  };

  const handleAddToLibrary = async () => {
    if (!deckId || !user) return;
    setIsAddToLibraryLoading(true);
    
    try {
      const success = await addPublicDeckToUser(user.uid, deckId);
      
      if (success) {
        setIsInUserLibrary(true);
        if (deck?.popularity !== undefined) {
          setDeck({
            ...deck,
            popularity: deck.popularity + 1
          });
        }
        
        setSnackbar({
          open: true,
          message: 'Deck added to your library!',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Could not add deck to your library.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error adding deck to library:', error);
      setSnackbar({
        open: true,
        message: 'An error occurred while adding the deck.',
        severity: 'error'
      });
    } finally {
      setIsAddToLibraryLoading(false);
    }
  };

  const handleOpenRemoveFromLibrary = () => {
    setIsRemoveFromLibraryDialogOpen(true);
  };

  const handleCloseRemoveFromLibrary = () => {
    setIsRemoveFromLibraryDialogOpen(false);
  };

  const handleRemoveFromLibrary = async () => {
    if (!deckId || !user) return;
    
    try {
      const success = await removeUserDeck(user.uid, deckId);
      
      if (success) {
        setIsInUserLibrary(false);
        setIsRemoveFromLibraryDialogOpen(false);
        
        setSnackbar({
          open: true,
          message: 'Deck removed from your library!',
          severity: 'success'
        });
        
        // Navigate back to decks page after removal
        navigate('/decks');
      } else {
        setSnackbar({
          open: true,
          message: 'Could not remove deck from your library.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error removing deck from library:', error);
      setSnackbar({
        open: true,
        message: 'An error occurred while removing the deck.',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="lg">
      {/* Header with deck-specific styling */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          mt: 3, 
          mb: 3, 
          borderTop: deck.fromClassId 
            ? '5px solid #3f51b5' // Blue border for class deck
            : '5px solid #2e7d32', // Green border for regular deck
          position: 'relative',
          backgroundColor: deck.fromClassId 
            ? '#f5f7ff' // Light blue background for class deck
            : '#f1f8e9' // Light green background for regular deck
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {deck.fromClassId ? (
            <ClassIcon sx={{ fontSize: 28, mr: 1, color: '#3f51b5' }} />
          ) : deck.isPublic ? (
            <PublicIcon sx={{ fontSize: 28, mr: 1, color: '#2e7d32' }} />
          ) : (
            <LockIcon sx={{ fontSize: 28, mr: 1, color: '#2e7d32' }} />
          )}
          <Typography variant="overline" sx={{ 
            fontWeight: 'bold', 
            letterSpacing: 1,
            color: deck.fromClassId ? '#3f51b5' : '#2e7d32'
          }}>
            {deck.fromClassId 
              ? t('decks.deckTypes.classDeck')
              : deck.isPublic 
                ? t('decks.deckTypes.publicDeck')
                : t('decks.deckTypes.privateDeck')
            }
          </Typography>
          {deck.fromClassId && (
            <Chip
              label={`${t('decks.deckTypes.fromClass')}: ${deck.fromClassName}`}
              size="small"
              color="primary"
              sx={{ ml: 2 }}
            />
          )}
        </Box>
        
        {/* Перемещаем кнопки управления наверх рядом с кнопкой "Все колоды" */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2
        }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/decks')}
          >
            {t('common.allDecks')}
          </Button>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row', 
            gap: 1
          }}>
            {isOwner ? (
              <>
                {isMobile ? (
                  <>
                    <IconButton
                      color="primary"
                      onClick={() => setIsEditDialogOpen(true)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={handleOpenDeleteDeckDialog}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                    <IconButton
                      color="success"
                      onClick={() => navigate(`/study/${deckId}`)}
                      size="small"
                    >
                      <PlayArrowIcon />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <Button
                      startIcon={<EditIcon />}
                      variant="outlined"
                      color="primary"
                      onClick={() => setIsEditDialogOpen(true)}
                    >
                      {t('decks.editDeck')}
                    </Button>
                    <Button
                      startIcon={<DeleteIcon />}
                      variant="outlined"
                      color="error"
                      onClick={handleOpenDeleteDeckDialog}
                      sx={{ mx: 1 }}
                    >
                      {t('decks.deleteDeck')}
                    </Button>
                    <Button
                      startIcon={<PlayArrowIcon />}
                      variant="contained"
                      color="success"
                      onClick={() => navigate(`/study/${deckId}`)}
                    >
                      {t('decks.study')}
                    </Button>
                  </>
                )}
              </>
            ) : !isInUserLibrary ? (
              isMobile ? (
                <IconButton
                  color="primary"
                  onClick={handleAddToLibrary}
                  disabled={isAddToLibraryLoading}
                  size="small"
                >
                  <LibraryAddIcon />
                </IconButton>
              ) : (
                <Button
                  startIcon={<LibraryAddIcon />}
                  variant="contained"
                  color="primary"
                  onClick={handleAddToLibrary}
                  disabled={isAddToLibraryLoading}
                >
                  {isAddToLibraryLoading ? t('common.loading') : t('decks.addToLibrary')}
                </Button>
              )
            ) : deck.fromClassId && deck.fromClassName ? (
              isMobile ? (
                <IconButton
                  color="primary"
                  component={RouterLink}
                  to={`/classes/${deck.fromClassId}`}
                  size="small"
                >
                  <ClassIcon />
                </IconButton>
              ) : (
                <Button
                  startIcon={<ClassIcon />}
                  variant="outlined"
                  color="primary"
                  component={RouterLink}
                  to={`/classes/${deck.fromClassId}`}
                >
                  {t('classes.viewClass')}: {deck.fromClassName}
                </Button>
              )
            ) : (
              isMobile ? (
                <IconButton
                  color="success"
                  onClick={handleOpenRemoveFromLibrary}
                  size="small"
                >
                  <LibraryAddCheckIcon />
                </IconButton>
              ) : (
                <Button
                  startIcon={<LibraryAddCheckIcon />}
                  variant="outlined"
                  color="success"
                  onClick={handleOpenRemoveFromLibrary}
                >
                  {t('decks.inLibrary')}
                </Button>
              )
            )}
            
            {!isOwner && (
              isMobile ? (
                <IconButton
                  color="success"
                  onClick={() => navigate(`/study/${deckId}`)}
                  size="small"
                >
                  <PlayArrowIcon />
                </IconButton>
              ) : (
                <Button
                  startIcon={<PlayArrowIcon />}
                  variant="contained"
                  color="success"
                  onClick={() => navigate(`/study/${deckId}`)}
                >
                  {t('decks.study')}
                </Button>
              )
            )}
          </Box>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', md: 'flex-start' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 0 }
        }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
              {deck.title}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {deck.description || t('decks.noDeckDescription')}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <PersonIcon sx={{ fontSize: 20, mr: 0.5, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {t('decks.author')}: {isOwner ? t('common.you') : authorName}
              </Typography>
            </Box>
          </Box>
        </Box>
        
        {/* Deck stats info */}
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 1, md: 3 }, 
          mt: 2, 
          pt: 2, 
          borderTop: '1px solid rgba(0, 0, 0, 0.12)',
          flexWrap: 'wrap'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={t('decks.cards')}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AddIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {cards.length} {t('decks.cards')}
                </Typography>
              </Box>
            </Tooltip>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={t('decks.learned')}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {cards.filter(card => card.isLearned).length} {t('decks.learned')}
                </Typography>
              </Box>
            </Tooltip>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={t('decks.lastStudied')}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AccessTimeIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {deck.lastStudied ? new Date(deck.lastStudied).toLocaleDateString() : t('common.never')}
                </Typography>
              </Box>
            </Tooltip>
          </Box>
          
          {deck.isPublic && deck.popularity !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title={t('decks.popularity')}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <StarIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {deck.popularity} {deck.popularity === 1 ? t('users.user') : t('users.users')}
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Card list and actions */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
          {deck?.tags.map(tag => (
            <Chip 
              key={tag} 
              label={tag} 
              size="small" 
              color="primary" 
              variant="outlined" 
            />
          ))}
        </Box>

        {isOwner && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Tooltip title={t('decks.addCard')}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setIsAddDialogOpen(true)}
                sx={{ 
                  minWidth: isMobile ? 'auto' : 'inherit',
                  px: isMobile ? 2 : 3
                }}
              >
                {!isMobile && t('decks.addCard')}
              </Button>
            </Tooltip>
            <Tooltip title={t('decks.importCards')}>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => setIsImportDialogOpen(true)}
                sx={{ 
                  minWidth: isMobile ? 'auto' : 'inherit',
                  px: isMobile ? 2 : 3
                }}
              >
                {!isMobile && t('decks.importCards')}
              </Button>
            </Tooltip>
            <Tooltip title={t('decks.clearAllCards')}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<ClearAllIcon />}
                onClick={() => setIsClearDialogOpen(true)}
                disabled={cards.length === 0}
                sx={{ 
                  minWidth: isMobile ? 'auto' : 'inherit',
                  px: isMobile ? 2 : 3
                }}
              >
                {!isMobile && t('decks.clearAllCards')}
              </Button>
            </Tooltip>
          </Box>
        )}

        {cards.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            py: 5
          }}>
            <Typography variant="h6" color="text.secondary" align="center">
              {t('decks.noCards')}
            </Typography>
            {isOwner && (
              <Tooltip title={t('decks.addCard')}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setIsAddDialogOpen(true)}
                  sx={{ 
                    mt: 2,
                    minWidth: isMobile ? 'auto' : 'inherit',
                    px: isMobile ? 2 : 3
                  }}
                >
                  {!isMobile && t('decks.addCard')}
                </Button>
              </Tooltip>
            )}
          </Box>
        ) : (
          <Box>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 2 
            }}>
              <Typography variant="h6" gutterBottom>
                {t('decks.cards')} ({cards.length})
              </Typography>
              
              {/* Добавляем подсказку для мобильных устройств */}
              {isMobile && isOwner && (
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    fontStyle: 'italic',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <SwipeRightIcon fontSize="small" />
                  {t('decks.swipeToDelete')}
                </Typography>
              )}
            </Box>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? `5px repeat(${deck?.sideNames.length}, 1fr)` : `40px repeat(${deck?.sideNames.length}, 1fr)`,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'grey.100',
              p: 2
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <StarIcon fontSize="small" color="action" />
              </Box>
              {deck?.sideNames.map((sideName, index) => (
                <Typography 
                  key={index} 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 'bold', 
                    px: 2,
                    fontSize: isMobile ? '0.8rem' : '1rem'
                  }}
                >
                  {sideName}
                </Typography>
              ))}
            </Box>
            {cards.map((card, index) => (
              <CardItem
                key={card.id}
                card={card}
                index={index}
                isDuplicateCard={isDuplicateCard}
                isOwner={isOwner}
                isMobile={isMobile}
                deckSideNames={deck?.sideNames || []}
                onDelete={(cardId) => {
                  setCardToDelete(cardId);
                  setIsDeleteDialogOpen(true);
                }}
                onUpdateCard={handleUpdateCard}
                onToggleStar={handleToggleStar}
                userStarredCards={userStarredCards}
              />
            ))}
          </Box>
        )}
      </Paper>

      <AddCardDialog
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onSave={handleAddCard}
        sideNames={deck?.sideNames || []}
      />

      <ImportCardsDialog
        open={isImportDialogOpen}
        onClose={() => setIsImportDialogOpen(false)}
        onImport={handleImportCards}
        sideCount={deck?.sideNames.length || 0}
        sideNames={deck?.sideNames || []}
      />

      <DeckForm
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        onSave={handleUpdateDeck}
        initialData={{
          title: deck?.title || '',
          description: deck?.description || '',
          sideNames: deck?.sideNames || [],
          tags: deck?.tags || [],
          isPublic: deck?.isPublic
        }}
        deckId={deckId}
      />

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>{t('cards.deleteCard')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('cards.confirmDeleteCard')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteCard}
            color="error"
            variant="contained"
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isClearDialogOpen}
        onClose={() => setIsClearDialogOpen(false)}
      >
        <DialogTitle>{t('decks.clearAllCards')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('decks.confirmClear')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsClearDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleClearDeck}
            color="error"
            variant="contained"
          >
            {t('common.clear')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Deck Dialog */}
      <Dialog
        open={isDeleteDeckDialogOpen}
        onClose={handleCloseDeleteDeckDialog}
      >
        <DialogTitle>{t('decks.deleteDeck')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('decks.confirmDelete')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDeckDialog}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteDeck}
            color="error"
            variant="contained"
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isRemoveFromLibraryDialogOpen}
        onClose={handleCloseRemoveFromLibrary}
      >
        <DialogTitle>{t('decks.removeFromLibrary')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('decks.confirmRemoveFromLibrary').replace('{title}', deck.title)}
          </Typography>
          <Typography variant="body2" color="primary" sx={{ mt: 2 }}>
            {t('decks.removeFromLibraryNote')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRemoveFromLibrary}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleRemoveFromLibrary} 
            color="warning"
            variant="contained"
          >
            {t('common.remove')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DeckDetailsPage;